// ─────────────────────────────────────────────────────────────
// Auth API — login/logout/me πάνω στο apiFetch.
// Η αποθήκευση ζει στο auth-storage.ts· εδώ μόνο οι κλήσεις.
// ─────────────────────────────────────────────────────────────

import { apiFetch } from "./api.ts"
import { accessChanged, clearAuth, getStoredUser, setAuth, setStoredUser, type AuthUser } from "./auth-storage.ts"

type LoginResponse = { token: string; user: AuthUser }

export async function login(email: string, password: string): Promise<AuthUser> {
  const res = await apiFetch<LoginResponse>("/login", "POST", { email, password })
  setAuth(res.token, res.user)
  return res.user
}

/**
 * Αποσύνδεση: ανακαλεί το token στον server και καθαρίζει τα πάντα τοπικά.
 * Το τοπικό καθάρισμα γίνεται ΠΑΝΤΑ — ακόμα κι αν ο server είναι κάτω,
 * ο χρήστης πρέπει να μπορεί να αποσυνδεθεί.
 */
export async function logout(): Promise<void> {
  try {
    await apiFetch<{ ok: boolean }>("/logout", "POST", {})
  } catch {
    // Ο server δεν απάντησε — το token θα λήξει ορφανό, αποδεκτό.
  } finally {
    clearAuth()
  }
}

/** Επιβεβαιώνει ότι το αποθηκευμένο token ισχύει ακόμα (κλήση στο refresh). */
export function fetchMe(): Promise<AuthUser> {
  return apiFetch<AuthUser>("/me")
}

/**
 * Ξαναδιαβάζει τα δικαιώματα από τον server και τα αποθηκεύει.
 *
 * ΓΙΑΤΙ ΥΠΑΡΧΕΙ: όταν ο διαχειριστής άλλαζε τον ρόλο κάποιου, ο χρήστης
 * έπρεπε να αποσυνδεθεί και να ξαναμπεί για να ισχύσει — τα δικαιώματα
 * γράφονταν στο localStorage τη στιγμή της σύνδεσης και δεν ξαναδιαβάζονταν
 * ποτέ. Το /me καλούνταν ήδη, αλλά η απάντησή του πεταγόταν.
 *
 * @returns true αν ΑΛΛΑΞΕ η πρόσβαση, οπότε η οθόνη πρέπει να ξαναχτιστεί.
 */
export async function refreshAccess(): Promise<boolean> {
  const fresh = await fetchMe()
  const changed = accessChanged(getStoredUser(), fresh)
  const stored = setStoredUser(fresh)

  // «Άλλαξε» σημαίνει «άλλαξε ΚΑΙ το κρατήσαμε». Αν η αποθήκευση απέτυχε, μια
  // ανανέωση θα ξανάβρισκε την ίδια διαφορά και θα ξανακατέβαινε — ατέρμονα.
  return changed && stored
}
